/**
 * GlobalFeatureFlags — runtime kill switches for critical subsystems.
 *
 * Pure client store backed by localStorage. Lets ops disable a subsystem
 * (push, cinematic, audio bursts, etc.) without a redeploy. Reads are sync
 * and cheap so callers can guard render paths inline.
 */
import { useSyncExternalStore } from "react";

export type StabilityFlag =
  | "push"
  | "cinematic"
  | "audioBursts"
  | "wakeLock"
  | "overlays"
  | "sponsorTakeovers"
  | "creatorSystems"
  | "worldCupOps"
  | "viralSystems"
  | "legacyPredictionMocks";

const KEY = "vs.flags.v1";
const DEFAULTS: Record<StabilityFlag, boolean> = {
  push: true,
  cinematic: true,
  audioBursts: true,
  wakeLock: true,
  overlays: true,
  sponsorTakeovers: true,
  creatorSystems: true,
  worldCupOps: true,
  viralSystems: true,
  // PR-40C — Legacy mock prediction surfaces (PredictionCard /
  // PredictionHubSheet). OFF by default so /quinielas + the real
  // MundialPredictionEngine own the prediction surface. Flip ON only
  // to temporarily restore the legacy mock UI; the components are
  // kept on disk for reference.
  legacyPredictionMocks: false,
};

let state: Record<StabilityFlag, boolean> = { ...DEFAULTS };
const listeners = new Set<() => void>();

function load() {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) state = { ...DEFAULTS, ...(JSON.parse(raw) as Record<StabilityFlag, boolean>) };
  } catch {
    /* noop */
  }
}
load();

function persist() {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* noop */ }
}

export function getFlag(f: StabilityFlag): boolean {
  return state[f];
}

export function getAllFlags(): Record<StabilityFlag, boolean> {
  return { ...state };
}

export function setFlag(f: StabilityFlag, enabled: boolean) {
  if (state[f] === enabled) return;
  state = { ...state, [f]: enabled };
  persist();
  listeners.forEach((l) => l());
}

export function useFlag(f: StabilityFlag): boolean {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => state[f],
    () => DEFAULTS[f],
  );
}

export function useAllFlags(): Record<StabilityFlag, boolean> {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => state,
    () => DEFAULTS,
  );
}
